/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AptosDailyAverageTransactionInfo } from '../models/AptosDailyAverageTransactionInfo';
import type { AptosTrxBalanceChangeListInfo } from '../models/AptosTrxBalanceChangeListInfo';
import type { AptosTrxCoinPopularListInfo } from '../models/AptosTrxCoinPopularListInfo';
import type { AptosTrxCoinSearchInfo } from '../models/AptosTrxCoinSearchInfo';
import type { AptosTrxEventInfo } from '../models/AptosTrxEventInfo';
import type { AptosTrxFunctionSearchInfo } from '../models/AptosTrxFunctionSearchInfo';
import type { AptosTrxListInfo } from '../models/AptosTrxListInfo';
import type { AptosTrxOverviewInfo } from '../models/AptosTrxOverviewInfo';
import type { AptosTrxPayloadInfo } from '../models/AptosTrxPayloadInfo';
import type { AptosTrxScrollListInfo } from '../models/AptosTrxScrollListInfo';
import type { AptosTrxSummaryInfo } from '../models/AptosTrxSummaryInfo';
import type { AptosUawTrxCountChartListInfo } from '../models/AptosUawTrxCountChartListInfo';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AptosTrxControllerService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns AptosTrxOverviewInfo OK
     * @throws ApiError
     */
    public getAptosTransactionDetail({
        version,
    }: {
        version: number,
    }): CancelablePromise<AptosTrxOverviewInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction',
            query: {
                'version': version,
            },
        });
    }

    /**
     * @returns AptosUawTrxCountChartListInfo OK
     * @throws ApiError
     */
    public getAptosUawTrxCount(): CancelablePromise<AptosUawTrxCountChartListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/uaw/count',
        });
    }

    /**
     * @returns AptosTrxSummaryInfo OK
     * @throws ApiError
     */
    public getAptosTransactionSummaryList({
        size = 10,
    }: {
        size?: number,
    }): CancelablePromise<Array<AptosTrxSummaryInfo>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/summary',
            query: {
                'size': size,
            },
        });
    }

    /**
     * @returns AptosTrxScrollListInfo OK
     * @throws ApiError
     */
    public getAptosTransactionScrollList({
        trxType = 'ALL',
        size = 25,
        lastVal,
        tp,
        stime,
        etime,
        from = '',
        to = '',
        fn = '',
        samt,
        eamt,
        cid = '',
    }: {
        trxType?: 'all' | 'genesis_transaction' | 'block_metadata_transaction' | 'user_transaction' | 'state_checkpoint_transaction' | 'validator_transaction' | 'pending_transaction' | 'block_epilogue_transaction',
        size?: number,
        lastVal?: number,
        tp?: Array<'all' | 'genesis_transaction' | 'block_metadata_transaction' | 'user_transaction' | 'state_checkpoint_transaction' | 'validator_transaction' | 'pending_transaction' | 'block_epilogue_transaction'>,
        stime?: number,
        etime?: number,
        from?: string,
        to?: string,
        fn?: string,
        samt?: number,
        eamt?: number,
        cid?: string,
    }): CancelablePromise<AptosTrxScrollListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/scroll/list',
            query: {
                'trxType': trxType,
                'size': size,
                'lastVal': lastVal,
                'tp': tp,
                'stime': stime,
                'etime': etime,
                'from': from,
                'to': to,
                'fn': fn,
                'samt': samt,
                'eamt': eamt,
                'cid': cid,
            },
        });
    }

    /**
     * @returns AptosTrxPayloadInfo OK
     * @throws ApiError
     */
    public getAptosTransactionPayloadList({
        version,
    }: {
        version: number,
    }): CancelablePromise<AptosTrxPayloadInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/payload',
            query: {
                'version': version,
            },
        });
    }

    /**
     * @returns AptosTrxListInfo OK
     * @throws ApiError
     */
    public getAptosTransactionList({
        trxType = 'ALL',
        page = 1,
        size = 25,
    }: {
        trxType?: 'all' | 'genesis_transaction' | 'block_metadata_transaction' | 'user_transaction' | 'state_checkpoint_transaction' | 'validator_transaction' | 'pending_transaction' | 'block_epilogue_transaction',
        page?: number,
        size?: number,
    }): CancelablePromise<AptosTrxListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/list',
            query: {
                'trxType': trxType,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public downloadCsvAptosTransactionList({
        address = '',
    }: {
        address?: string,
    }): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/list/csv',
            query: {
                'address': address,
            },
        });
    }

    /**
     * @returns AptosTrxFunctionSearchInfo OK
     * @throws ApiError
     */
    public getAptosTransactionFunctionFilterSearch({
        term,
    }: {
        term: string,
    }): CancelablePromise<AptosTrxFunctionSearchInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/function/search',
            query: {
                'term': term,
            },
        });
    }

    /**
     * @returns AptosTrxEventInfo OK
     * @throws ApiError
     */
    public getAptosTransactionEventList({
        version,
    }: {
        version: number,
    }): CancelablePromise<Array<AptosTrxEventInfo>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/event/list',
            query: {
                'version': version,
            },
        });
    }

    /**
     * @returns AptosDailyAverageTransactionInfo OK
     * @throws ApiError
     */
    public getAptosDailyAverageTransaction({
        period,
    }: {
        period: number,
    }): CancelablePromise<AptosDailyAverageTransactionInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/daily',
            query: {
                'period': period,
            },
        });
    }

    /**
     * @returns AptosTrxCoinSearchInfo OK
     * @throws ApiError
     */
    public getAptosTransactionCoinFilterSearch({
        term,
    }: {
        term: string,
    }): CancelablePromise<AptosTrxCoinSearchInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/coin/search',
            query: {
                'term': term,
            },
        });
    }

    /**
     * @returns AptosTrxCoinPopularListInfo OK
     * @throws ApiError
     */
    public getAptosTransactionCoinPopularList(): CancelablePromise<AptosTrxCoinPopularListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/coin/popular',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public getAptosTransactionChangeList({
        version,
    }: {
        version: number,
    }): CancelablePromise<Array<Record<string, Record<string, any>>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/change/list',
            query: {
                'version': version,
            },
        });
    }

    /**
     * @returns AptosTrxBalanceChangeListInfo OK
     * @throws ApiError
     */
    public getTrxBalanceChangeList1({
        version,
        page = 1,
        size = 5,
    }: {
        version: number,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosTrxBalanceChangeListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/transaction/balance/change/list',
            query: {
                'version': version,
                'page': page,
                'size': size,
            },
        });
    }

}
